/**
 * RoutineHeaderControls — the routine thread's header meta line + its ONE
 * inline pause/resume chip (production feedback 2026-08-01).
 *
 * Production RCA: a paused routine's header read "Routine · Every weekday at
 * 7:30 AM (Pacific) · Paused" with NO affordance to resume — the user typed
 * "resume" into the chat, which then mis-routed (fixed separately by the
 * requestedWorkflow thread contract). The header now carries exactly one
 * small action chip at the end of the meta line:
 *   • paused → "Resume" (primary filled emphasis — this is the fix for the
 *     stranded-paused state)
 *   • active → "Pause" (subtle outline)
 *
 * Click → the existing routines client (pauseRoutine/resumeRoutine) with an
 * OPTIMISTIC status flip through `onRoutineMirrorPatch` (the host's
 * threadStore.patchThreadRoutineMirror — local-only; the index-mirror
 * subscription reconciles server truth on top). In-flight disables the chip;
 * failure reverts the flip and shows an inline error. The whole component is
 * presentational + one bounded action — extracted from GlobalChatSystem's
 * header IIFE so it is directly testable.
 */
import React, { useCallback, useState } from 'react';
import {
  RoutineGlyph,
  routineNextRunLabel,
  routineNextRunLabelCompact,
  compactRoutineScheduleSummary,
} from './RoutineGlyph';
import {
  RoutineHeaderMeta,
  RoutineHeaderMetaText,
  RoutineHeaderActionChip,
  RoutineHeaderActionError,
} from './Routines.styles';
import { pauseRoutine, resumeRoutine } from '../../services/routines/client';
import type { ThreadRoutineMirrorPatch } from './useChatThreads';

export interface RoutineHeaderControlsProps {
  /** The active routine thread's id. */
  threadId: string;
  /**
   * v2 (2026-08-16): the PRIMARY routine's id from the thread mirror — the
   * pause/resume chip's action target. Legacy mirrors carry the threadId
   * here; absent falls back to threadId (pre-v2 servers resolve it).
   */
  routineId?: string;
  /**
   * v2: how many routines this thread holds. With more than one, the header
   * shows the count and DROPS the single pause/resume chip — one chip cannot
   * honestly represent several routines; the manager panel (or asking in the
   * thread) manages them individually.
   */
  routineCount?: number;
  /** Current mirror status — drives label, glyph, and which action fires. */
  paused: boolean;
  /** "Every weekday at 7:30 AM (Pacific)" — from the routine mirror. */
  scheduleSummary?: string;
  /** ISO next-run stamp (null/absent while paused). */
  nextRunAt?: string | null;
  /**
   * Host patch into the LOCAL thread record's routine mirror
   * (threadStore.patchThreadRoutineMirror). Called optimistically on click,
   * with the response echo on success, and with the revert on failure.
   */
  onRoutineMirrorPatch: (threadId: string, patch: ThreadRoutineMirrorPatch) => void;
  /**
   * Docked density round (2026-08-01): true in the DOCKED panel, where the
   * header's meta line must be compact — the "Routine" word drops (the glyph
   * already announces it), the schedule summary shortens ("Every 15 min")
   * and the next-run label compresses ("next 12m"). The pause/resume chip
   * is identical in both forms and NEVER truncates (Routines.styles
   * chip-never-truncates contract). Fullscreen/floating keep the full line.
   */
  compact?: boolean;
}

export const RoutineHeaderControls: React.FC<RoutineHeaderControlsProps> = ({
  threadId,
  routineId,
  routineCount,
  paused,
  scheduleSummary,
  nextRunAt,
  onRoutineMirrorPatch,
  compact = false,
}) => {
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAction = useCallback(async () => {
    if (inFlight) return;
    // Capture the pre-click state: the optimistic flip re-renders this
    // component with the OPPOSITE `paused` prop mid-request.
    const wasPaused = paused;
    setInFlight(true);
    setError(null);
    // Optimistic flip — header, sidebar row, and manager all render from the
    // same patched record; the index-mirror subscription reconciles later.
    onRoutineMirrorPatch(threadId, { routineStatus: wasPaused ? 'active' : 'paused' });
    try {
      const actionTarget = routineId || threadId;
      const result = wasPaused ? await resumeRoutine(actionTarget) : await pauseRoutine(actionTarget);
      const echo = result.routine;
      if (echo && echo.threadId === threadId) {
        // The response echo carries POST-action truth (a resume recomputes
        // nextRunAtMs server-side) — settle the mirror on it.
        onRoutineMirrorPatch(threadId, {
          routineStatus: echo.status,
          routineNextRunAt: typeof echo.nextRunAtMs === 'number'
            ? new Date(echo.nextRunAtMs).toISOString()
            : null,
          ...(echo.name ? { routineName: echo.name } : {}),
          ...(echo.scheduleSummary ? { routineScheduleSummary: echo.scheduleSummary } : {}),
        });
      }
    } catch {
      // Revert the optimistic flip — the routine's server state is unchanged.
      onRoutineMirrorPatch(threadId, { routineStatus: wasPaused ? 'paused' : 'active' });
      setError(wasPaused
        ? 'Could not resume this routine — please try again.'
        : 'Could not pause this routine — please try again.');
    } finally {
      setInFlight(false);
    }
  }, [inFlight, paused, threadId, routineId, onRoutineMirrorPatch]);

  // Docked density round (2026-08-01): compact form drops the "Routine" word
  // (the glyph announces it) and compresses schedule + next-run so the
  // docked pane's ONE meta line fits beside the never-truncating chip.
  // v2: an active TRIGGER routine has no clock — the summary IS the cadence
  // ("Every time a new email arrives"), so an empty next-run label is fine.
  const multi = (routineCount ?? 1) > 1;
  const metaLine = compact
    ? [
        multi ? `${routineCount} routines` : compactRoutineScheduleSummary(scheduleSummary),
        paused ? 'Paused' : routineNextRunLabelCompact(nextRunAt),
      ].filter(Boolean).join(' · ')
    : [
        multi ? `${routineCount} routines` : 'Routine',
        multi ? '' : (scheduleSummary || ''),
        paused ? 'Paused' : routineNextRunLabel(nextRunAt),
      ].filter(Boolean).join(' · ');

  return (
    <>
      <RoutineHeaderMeta data-routine-header-meta="" data-routine-meta-compact={compact ? 'true' : 'false'}>
        <RoutineGlyph active={!paused} size={11} />
        {/* Ellipsizing TEXT span — the chip below stays whole under width
            pressure (chip-never-truncates contract, Routines.styles). */}
        <RoutineHeaderMetaText data-routine-meta-text="">{metaLine}</RoutineHeaderMetaText>
        {!multi && (
        <RoutineHeaderActionChip
          type="button"
          data-routine-header-action={paused ? 'resume' : 'pause'}
          $primary={paused}
          disabled={inFlight}
          aria-label={paused ? 'resume this routine' : 'pause this routine'}
          title={paused ? 'resume this routine' : 'pause this routine'}
          onClick={() => { void handleAction(); }}
        >
          {paused ? 'Resume' : 'Pause'}
        </RoutineHeaderActionChip>
        )}
      </RoutineHeaderMeta>
      {error && (
        <RoutineHeaderActionError role="alert" data-routine-header-action-error="">
          {error}
        </RoutineHeaderActionError>
      )}
    </>
  );
};

export default RoutineHeaderControls;
